import { ChevronDownIcon, ChevronUpIcon } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'

import { useMobile } from '@/hooks'

import { NoResults } from '../../../src/components/NoResults/NoResults'
import IndeterminateCheckbox from './IndeterminateCheckbox'
import { TableRows } from './TableRows'

/**
 * Converts react-table v7 column definitions to v8 format so callers don't
 * need to be migrated all at once.
 *
 * v7 uses: Header, accessor (string or fn), Cell, width/minWidth/maxWidth
 * v8 uses: header, accessorKey / accessorFn (+ id), cell, size/minSize/maxSize
 */
let _colIdCounter = 0

function normalizeColumns<T>(columns: ColumnDef<T, any>[]): ColumnDef<T, any>[] {
  return columns.map((col: any) => {
    // Detect whether any v7-only keys are present
    const hasV7Keys = col.Header !== undefined || col.accessor !== undefined || col.Cell !== undefined

    // Pure v8 column — only pass through if no v7 keys present
    if (!hasV7Keys) return col

    // Start from existing column def so v8 keys (accessorKey, etc.) are preserved
    const v8: any = { ...col }

    // Header → header (only if v8 header not already set)
    if (col.Header !== undefined && col.header === undefined) v8.header = col.Header
    delete v8.Header

    // accessor: string → accessorKey (dot-notation → accessorFn + id)
    if (typeof col.accessor === 'string') {
      if (col.accessor.includes('.')) {
        const keys = col.accessor.split('.')
        v8.accessorFn = (row: any) => keys.reduce((obj: any, k: string) => obj?.[k], row)
        v8.id = col.id ?? col.accessor
      } else {
        v8.accessorKey = col.accessor
      }
      delete v8.accessor
    }

    // accessor: function → accessorFn + stable auto id
    if (typeof col.accessor === 'function') {
      v8.accessorFn = col.accessor
      v8.id =
        col.id ??
        (typeof col.Header === 'string' ? col.Header.toLowerCase().replace(/\s+/g, '_') : undefined) ??
        `col_${++_colIdCounter}`
      delete v8.accessor
    }

    // Cell → cell (bridge { value } → getValue())
    // Always run this bridge when v7 Cell is present, even if accessorKey/accessorFn already set.
    if (col.Cell) {
      const V7Cell = col.Cell
      v8.cell = (ctx: any) =>
        V7Cell({ value: ctx.getValue?.(), row: ctx.row, column: ctx.column, table: ctx.table })
      delete v8.Cell
    }

    // Size props
    if (col.width !== undefined) { v8.size = col.width; delete v8.width }
    if (col.minWidth !== undefined) { v8.minSize = col.minWidth; delete v8.minWidth }
    if (col.maxWidth !== undefined) { v8.maxSize = col.maxWidth; delete v8.maxWidth }

    return v8
  })
}

interface TableProps<T extends object> {
  className?: string
  columns: ColumnDef<T, any>[]
  data: T[]
  disableSortBy?: boolean
  manualSortBy?: boolean
  renderNoData?(): React.ReactElement | null
  rowSelection?: boolean
  setRowSelection?: (row: Row<T>[]) => void
  setSortBySelection?: (sortBy: { id: string; desc: boolean }[]) => void
  children?: React.ReactNode
}

export function Table<T extends object>({
  children,
  renderNoData,
  className = '',
  setRowSelection: setRowSelectionProp,
  setSortBySelection,
  rowSelection = false,
  disableSortBy = true,
  manualSortBy = false,
  columns,
  data,
}: PropsWithChildren<TableProps<T>>) {
  const { isMobile } = useMobile()

  const [rowSelectionState, setRowSelectionState] = useState<Record<string, boolean>>({})
  const [sorting, setSorting] = useState<SortingState>([])

  const selectionColumn: ColumnDef<T, any> = {
    id: 'selection',
    size: 40,
    maxSize: 40,
    header: ({ table }) => (
      <IndeterminateCheckbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        name="header"
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <div className="w-6">
        <IndeterminateCheckbox
          checked={row.getIsSelected()}
          indeterminate={row.getIsSomeSelected()}
          name={`row-${row.id}`}
          onChange={row.getToggleSelectedHandler()}
        />
      </div>
    ),
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allColumns: ColumnDef<T, any>[] = useMemo(
    () => normalizeColumns(rowSelection ? [selectionColumn, ...columns] : columns),
    // selectionColumn is stable (only depends on rowSelection which is in the array)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowSelection, columns],
  )

  const table = useReactTable({
    data,
    columns: allColumns,
    state: {
      rowSelection: rowSelectionState,
      sorting,
    },
    enableRowSelection: true,
    enableSorting: !disableSortBy,
    manualSorting: manualSortBy,
    onRowSelectionChange: setRowSelectionState,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const tableRef = useRef(table)
  tableRef.current = table

  useEffect(() => {
    if (setRowSelectionProp) {
      const selectedRows = tableRef.current.getSelectedRowModel().rows
      setRowSelectionProp(selectedRows)
    }
  }, [rowSelectionState, setRowSelectionProp])

  useEffect(() => {
    if (setSortBySelection) {
      setSortBySelection(sorting.map((s) => ({ id: s.id, desc: s.desc ?? false })))
    }
  }, [sorting, setSortBySelection])

  function renderHeaderGroups() {
    return table.getHeaderGroups().map((headerGroup) => (
      <tr className="flex h-14 border-b bg-grey-200 px-6  border-secondary " key={headerGroup.id}>
        {headerGroup.headers.map((header, i) => {
          const canSort = header.column.getCanSort()
          const isSorted = header.column.getIsSorted()
          return (
            <th
              className="flex shrink-0 items-center text-left [font-weight:500] justify-start"
              key={header.id}
              onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
              style={{ cursor: canSort ? 'pointer' : undefined, flex: `${header.getSize()} 0 0%` }}
            >
              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

              {isSorted && (
                <>
                  {isSorted === 'desc' ? (
                    <ChevronDownIcon className="ml-1" width={14} />
                  ) : (
                    <ChevronUpIcon className="ml-1" width={14} />
                  )}
                </>
              )}
            </th>
          )
        })}
      </tr>
    ))
  }

  return (
    <div className={`table-container overflow-hidden rounded-lg border border-secondary md:w-full ${className}`}>
      {children}
      {!data.length && renderNoData ? (
        renderNoData()
      ) : (
        <div className="table-wrapper w-screen md:w-full">
          <TableStyled className="w-full">
            {!isMobile && <thead>{renderHeaderGroups()}</thead>}

            <tbody>
              {!data.length ? (
                <tr className="flex w-full flex-col-reverse md:items-center md:justify-center" key="row_header">
                  <td>
                    <NoResults />
                  </td>
                </tr>
              ) : (
                <>
                  <TableRows rows={table.getRowModel().rows} />
                </>
              )}
            </tbody>
          </TableStyled>
        </div>
      )}
    </div>
  )
}

const TableStyled = styled.table`
  border-spacing: 0;

  .thead {
    overflow-y: auto;
    overflow-x: hidden;
  }

  tbody {
    overflow-y: auto;
    overflow-x: hidden;
    display: block;
    max-height: calc(100vh - 250px);

    tr {
      &:hover {
        background-color: #f3f3f2;
      }

      .route-link {
        &:hover {
          p {
            color: #08786c;
          }
        }
      }
    }

    th,
    td {
      position: relative;
    }
  }
  .resizer {
    background-color: #cecdca;
    height: 100%;
    right: 0;
    top: 0;
    position: absolute;
    width: 1px;
    z-index: 1;
    touch-action: none;
    &.isResizing {
      background: black;
    }
  }
`
